import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { data as dataApi, tasks } from '../../lib/api'
import { ActionRecord, DuplicateRow, MetaEntry, WorkflowConfig, WorkflowStep } from '../../lib/types'
import Loading from '../../components/loading'
import { getWorkflowConfig } from '../../workflows/util'
import Form from '../../components/form'
import Alert from '../../components/alert'
import ErrorMsg from '../../components/error-msg'
import advanceStep from '../advance'
import MetaSummary from '../meta-summary'
import { Button } from '@carbon/react'
import WorkflowHistoryModal from '../../components/modal/WorkflowHistoryModal'
import SimilarRowTable from './similar-row-table'
import getId from '../../lib/get-id'

export default function ReviewRowPage() {
    const { id, stepIndex, rowId, srcId = 0, user } = useParams()
    const [row, setRow] = useState<any>()
    const [prevRow, setPrevRow] = useState<any>()
    const [similarRow, setSimilarRow] = useState<any>()
    const [similarRowMeta, setSimilarRowMeta] = useState<MetaEntry | null>()
    const [similarRowReview, setSimilarRowReview] = useState<ActionRecord>()
    const [duplicateRow, setDuplicateRow] = useState<DuplicateRow | undefined>()
    const [meta, setMeta] = useState<MetaEntry | null>(null)
    const [review, setReview] = useState<ActionRecord>()
    const [step, setStep] = useState<WorkflowStep>()
    const [loading, setLoading] = useState(true)
    const [note, setNote] = useState('')
    const [hasError, setHasError] = useState(false)
    const [saving, setSaving] = useState(false)
    const [err, setErr] = useState('')
    const [config, setConfig] = useState<WorkflowConfig>()
    const [openNotesHistory, setOpenNotesHistory] = useState(false)
    const navigate = useNavigate()


    useEffect(() => {
        setLoading(true)
        dataApi
            .getUser(Number(id), Number(stepIndex), user!, Number(rowId), Number(srcId))
            .then(({ row, meta, review, prevRow, similarRow, similarRowMeta, similarRowReview, duplicate }) => {
                setRow(row)
                setPrevRow(prevRow)
                setSimilarRow(similarRow)
                setSimilarRowMeta(similarRowMeta)
                setSimilarRowReview(similarRowReview)
                setMeta(meta)
                setReview(review)
                setDuplicateRow(duplicate)
                setLoading(false)
            })
            .catch((err) => setErr(err.message || err))

        getWorkflowConfig(Number(id))
            .then((config) => {
                setConfig(config)
                // Find the previous input step to determine the view
                let inputStep: WorkflowStep | undefined
                for (let i = Number(stepIndex) - 1; i >= 0; i--) {
                    let step = config.steps[i]
                    if (
                        step.type === 'input' ||
                        step.type === 'input-cycle' ||
                        step.type === 'correct' ||
                        step.type === 'correct-cycle'
                    ) {
                        inputStep = step
                        break
                    }
                }
                setStep(inputStep)
            })
            .catch((err) => setErr(err.message || err))
    }, [id, stepIndex, rowId, srcId, user])

    async function approve() {
        setSaving(true)
        await tasks.approve(Number(id), Number(stepIndex), user!, meta?.objId! ,getId(row))
        if (step?.autoAdvance) navigate(advanceStep(config!, id!, stepIndex!, rowId!))
        else navigate(`/workflows/${id}/steps/${stepIndex}`)
    }
    async function flagError() {
        setSaving(true)
        await tasks.flagError(
            Number(id),
            Number(stepIndex),
            user!,
            meta?.objId!,
            note || undefined,
        )
        if (similarRow && similarRowMeta) {
            const userId = String(similarRowMeta.accountId) 
            await tasks.flagError(
                Number(id),
                Number(stepIndex),
                userId,
                similarRowMeta.objId!,
                note || undefined,
            )
        }
        navigate(`/workflows/${id}/steps/${stepIndex}`)
    }
    async function markDuplicate() {
        setSaving(true)
        try {
            await tasks.markAsDuplicate(
                Number(id),
                Number(stepIndex),
                user!,
                Number(rowId),
                note || undefined,
            )
        } catch (e: any) {
            setErr(e.message || e)
        }
        navigate(`/workflows/${id}/steps/${stepIndex}`)
    }

    const showNotesHistory = () => {
        setOpenNotesHistory(true)
    }

    const onClickRow = (row: any, objId: number, accountId: number, modKey: boolean) => {
        let path = `/workflows/${id}/steps/${stepIndex}/review/${accountId}/${objId}/${getId(row)}`
        if (modKey) window.open(window.location.origin + path, '_blank')
        else navigate(path)
    }

    const normalizeURL = (url: string) => {
        const trimmedUrl = url.trim()
        if (trimmedUrl.startsWith('https://') || trimmedUrl.startsWith('http://')) {
            return trimmedUrl
        }
        return "https://" + trimmedUrl
    }

    return (
        <div className='container mx-auto mt-10 pb-5'>
            <div className='mb-3'>
                <Link to={`/workflows/${id}/steps/${stepIndex}`}>
                    <i className='lni lni-chevron-left' /> Back to Data
                </Link>
            </div>
            <h1>Review Row ({user})</h1>

            {err && <ErrorMsg error={err} />}
            {loading ? (
                <Loading />
            ) : (
                <div>
                    {step ? (
                        <div>
                            {review && (
                                <Alert>
                                    <span className='text-orange-400 font-bold mr-2'>
                                        <i className='lni lni-warning' /> Notice
                                    </span>
                                    This row has already been reviewed on{' '}
                                    {new Date(review.actionTime).toLocaleString()}.
                                </Alert>
                            )}
                            {similarRowReview && (<Alert>
                                <span className='text-orange-400 font-bold mr-2'>
                                    <i className='lni lni-warning' /> Notice
                                </span>
                                It's similar row has already been reviewed on{' '}
                                {new Date(similarRowReview.actionTime).toLocaleString()}.
                            </Alert>
                            )}
                            {!!duplicateRow && (<Alert>
                                <span className='text-orange-400 font-bold mr-2'>
                                    <i className='lni lni-warning' /> Notice
                                </span>
                                This row has already been marked as duplicate on{' '}
                                {new Date(duplicateRow.date).toLocaleString()}.
                            </Alert>
                            )}
                            {config && meta && <MetaSummary meta={meta} config={config} onClickShowHistory={showNotesHistory} />}
                            {similarRow && similarRowMeta && row && meta && <SimilarRowTable config={step.view!} similarRow={similarRow}
                                similarRowMeta={similarRowMeta} onClick={onClickRow} data={row} meta={meta} />}
                            <Form config={step.view!} data={row} prevData={prevRow} readOnly />
                            <hr className='my-5' />
                            {meta?.sources && (
                                <div className='mb-3'>
                                    <label className='font-bold text-blue-700'>Sources </label>
                                    {meta.sources.split(',').map((url, index) => (
                                        <div>
                                            <a
                                                key={index}
                                                href={normalizeURL(url)}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                            >
                                                {url.trim()}
                                            </a>

                                        </div>
                                    ))}
                                </div>
                            )}
                            <label>
                                <input
                                    type='checkbox'
                                    checked={hasError}
                                    onChange={(e) => setHasError(e.target.checked)}
                                />
                                <span className='ml-3 text-gray-600'>Has data error</span>
                            </label>

                            {hasError && (
                                <textarea
                                    placeholder='Enter notes...'
                                    className='notes h-32'
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                />
                            )}

                            <div className='mt-3'>
                                {hasError ? (
                                    <Button
                                        className='red ml-2'
                                        size='sm'
                                        disabled={!note || saving || Boolean(similarRowReview) || meta?.error || !!duplicateRow}
                                        onClick={flagError}
                                    >
                                        {saving ? 'Saving...' : 'Flag Error'}
                                    </Button>
                                ) : (
                                    <Button 
                                        className='blue ml-2' size='sm' 
                                        disabled={saving || Boolean(similarRowReview) || meta?.error || !!duplicateRow} 
                                        onClick={approve}
                                    >
                                        {saving ? 'Saving...' : 'Mark as Reviewed'}
                                    </Button>
                                )}
                                <Button
                                    size='sm'
                                    className='red ml-2'
                                    disabled={saving || Boolean(similarRowReview) || meta?.error || !!duplicateRow}
                                    onClick={markDuplicate}
                                >
                                    {saving ? 'Saving...' : 'Mark as Duplicate'}
                                </Button>
                                <Link
                                    className='button ml-2'
                                    to={`/workflows/${id}/steps/${stepIndex}`}
                                >
                                    Cancel
                                </Link>                          
                            </div>
                            <div className='flex flex-col mt-3'>
                                {hasError && !note && (
                                    <span className='text-yellow-500 ml-3 text-sm'>
                                        Please enter a note to flag the error.
                                    </span>
                                )}                    
                                {meta?.error && (
                                    <span className='text-yellow-500 ml-3 text-sm'>
                                        Errors must be cleared by the user before the row can be marked as reviewed.
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div>Could not find an input step to render the data with.</div>
                    )}
                </div>
            )}
            <WorkflowHistoryModal open={openNotesHistory} onClose={() => setOpenNotesHistory(false)} workflowId={Number(id)} rowId={Number(rowId)} />
        </div>
    )
}
